import React, { useState, useEffect, forwardRef, useRef, useCallback } from 'react';
import { Table, Button, Modal, Form, Input, message, Tag, Space, Upload, Image } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import axios from 'axios';
import './index.css';

// 注意：需要安装 react-quill: npm install react-quill
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// 创建一个自定义组件来包装 ReactQuill，避免 findDOMNode 警告
const CustomQuill = forwardRef(({ value, onChange, ...props }, ref) => {
  const quillRef = useRef(null);
  
  // 处理图片上传（不需要富文本change就上传， 与form一起提交上传）
  // const handleImageUpload = useCallback(() => {
  //   const input = document.createElement('input');
  //   input.setAttribute('type', 'file');
  //   input.setAttribute('accept', 'image/*');
  //   input.click();
    
  //   input.onchange = async () => {
  //     const file = input.files[0];
  //     if (!file) return;
      
  //     const formData = new FormData();
  //     formData.append('image', file);
      
  //     try {
  //       const response = await axios.post('/api/articles/upload-quill-image', formData, {
  //         headers: {
  //           'Content-Type': 'multipart/form-data'
  //         }
  //       });
        
  //       if (response.data.success) {
  //         const quill = quillRef.current.getEditor();
  //         const range = quill.getSelection();
  //         quill.insertEmbed(range.index, 'image', response.data.data.url);
  //       } else {
  //         message.error(response.data.message || '图片上传失败');
  //       }
  //     } catch (error) {
  //       message.error('图片上传失败: ' + error.message);
  //     }
  //   };
  // }, []);
  
  // 自定义工具栏
  const modules = React.useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        ['link', 'image'],
        ['clean']
      ],
      // handlers: {
      //   image: handleImageUpload
      // }
    }
  }), []); // handleImageUpload

  return (
    <ReactQuill
      ref={quillRef}
      theme="snow"
      value={value}
      onChange={onChange}
      modules={modules}
      {...props}
    />
  );
});

CustomQuill.displayName = 'CustomQuill';

const Recruitment = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [form] = Form.useForm();
  const [coverImage, setCoverImage] = useState(null);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [content, setContent] = useState('');
  const [quillKey, setQuillKey] = useState(0); // 用于确保组件稳定


  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/articles');
      if (response.data.success) {
        setArticles(response.data.data);
      } else {
        message.error(response.data.message || '获取文章列表失败');
      }
    } catch (error) {
      message.error('获取文章列表失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingArticle(null);
    setCoverImage(null);
    setCoverImageFile(null);
    setContent('');
    setQuillKey(prev => prev + 1); // 更新 key 以确保组件重新渲染
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setCoverImage(article.cover_image);
    setCoverImageFile(null);
    setContent(article.content);
    setQuillKey(prev => prev + 1); // 更新 key 以确保组件重新渲染
    form.setFieldsValue({
      title: article.title
    });
    setModalVisible(true);
  };

  const handleDelete = (articleId) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这篇文章吗？',
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await axios.delete(`/api/articles/${articleId}`);
          if (response.data.success) {
            message.success('删除成功');
            fetchArticles();
          } else {
            message.error(response.data.message || '删除失败');
          }
        } catch (error) {
          message.error('删除失败: ' + error.message);
        }
      }
    });
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      // 使用 FormData 上传数据
      const formData = new FormData();
      formData.append('title', values.title);
      formData.append('content', content);
      
      // 添加封面图片文件
      if (coverImageFile) {
        formData.append('cover_image', coverImageFile);
      } else if (editingArticle && editingArticle.cover_image && !coverImage) {
        // 如果是编辑模式且原来有封面图，但现在移除了，则需要特殊处理
        // 这里可以添加一个字段来标识删除封面图
        formData.append('remove_cover_image', 'true');
      }

      let response;
      if (editingArticle) {
        // 更新文章
        response = await axios.put(`/api/articles/${editingArticle.id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        // 创建文章
        response = await axios.post('/api/articles', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      if (response.data.success) {
        message.success(editingArticle ? '更新成功' : '创建成功');
        setModalVisible(false);
        form.resetFields();
        fetchArticles();
      } else {
        message.error(response.data.message || (editingArticle ? '更新失败' : '创建失败'));
      }
    } catch (error) {
      message.error('操作失败: ' + error.message);
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    form.resetFields();
    setCoverImage(null);
    setCoverImageFile(null);
    setContent('');
  };

  const handleCoverImageChange = ({ fileList }) => {
    if (fileList.length > 0) {
      const file = fileList[0].originFileObj;
      if (file) {
        setCoverImageFile(file);
        // 显示预览
        const reader = new FileReader();
        reader.onload = (e) => {
          setCoverImage(e.target.result);
        };
        reader.readAsDataURL(file);
      }
    } else {
      setCoverImage(null);
      setCoverImageFile(null);
    }
  };

  // 使用 useCallback 缓存内容变化处理
  const handleContentChange = useCallback((newContent) => {
    setContent(newContent);
  }, []);

  const columns = [
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: '封面图',
      dataIndex: 'cover_image',
      key: 'cover_image',
      render: (coverImage) => coverImage ? <Image src={coverImage} width={50} height={50} /> : '无',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
          <Button icon={<DeleteOutlined />} danger onClick={() => handleDelete(record.id)}>删除</Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="recruitment-container">
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          添加文章
        </Button>
      </div>
      
      <Table
        dataSource={articles}
        columns={columns}
        loading={loading}
        rowKey="id"
        pagination={{
          pageSize: 10,
        }}
      />

      <Modal
        title={editingArticle ? "编辑文章" : "添加文章"}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={800}
        destroyOnClose={false}
        afterOpenChange={(open) => {
          // 当 Modal 打开时，确保富文本编辑器正确渲染
          if (open) {
            // 延迟一小段时间确保 DOM 已经渲染
            setTimeout(() => {
              window.dispatchEvent(new Event('resize'));
            }, 100);
          }
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="title"
            label="文章标题"
            rules={[{ required: true, message: '请输入文章标题' }]}
          >
            <Input placeholder="请输入文章标题" />
          </Form.Item>
          
          <Form.Item
            name="cover_image"
            label="封面图"
          >
            <Upload
              listType="picture-card"
              fileList={coverImage ? [{ uid: '-1', name: 'cover.jpg', status: 'done', url: coverImage }] : []}
              onChange={handleCoverImageChange}
              beforeUpload={() => false} // 阻止自动上传
            >
              {coverImage ? null : (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>上传</div>
                </div>
              )}
            </Upload>
          </Form.Item>
        </Form>
        
        <div style={{ marginTop: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>文章内容</label>
          {/* 使用动态 key 确保组件稳定 */}
          <CustomQuill 
            key={`quill-editor-${quillKey}`} // 使用动态 key
            value={content} 
            onChange={handleContentChange}
            style={{ height: '250px' }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default Recruitment;